import { GetObjectCommand } from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { s3Client } from "../../../../Base/helper/baseFunctions";

/**
 * Get S3 presigned URL for letterhead PDF or return direct URL
 */
export const getLetterheadSignedUrl = async (filePath) => {
  if (!filePath) return null;
  if (
    filePath.startsWith("http://") ||
    filePath.startsWith("https://") ||
    filePath.startsWith("blob:") ||
    filePath.startsWith("data:")
  ) {
    return filePath;
  }
  try {
    const bucketParams = {
      Bucket: process.env.REACT_APP_DO_BUCKET_NAME,
      Key: filePath,
    };
    return await getSignedUrl(s3Client, new GetObjectCommand(bucketParams), { expiresIn: 3600 });
  } catch (err) {
    console.error("[OfferLetterEditor] Error generating presigned URL for letterhead:", err);
    return null;
  }
};

/**
 * Render the first page of a letterhead PDF as high-resolution PNG data URL using PDF.js
 */
export const loadPdfFirstPageAsDataUrl = async (pdfUrl) => {
  if (!pdfUrl) return null;
  try {
    if (!window.pdfjsLib) {
      await new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
        script.onload = () => {
          if (window.pdfjsLib) {
            window.pdfjsLib.GlobalWorkerOptions.workerSrc =
              "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
          }
          resolve();
        };
        script.onerror = (e) => reject(e);
        document.head.appendChild(script);
      });
    }
    const loadingTask = window.pdfjsLib.getDocument(pdfUrl);
    const pdfDoc = await loadingTask.promise;
    const page = await pdfDoc.getPage(1);
    const scale = 2.0;
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext("2d");
    await page.render({ canvasContext: ctx, viewport }).promise;
    return canvas.toDataURL("image/png");
  } catch (err) {
    console.warn("[OfferLetterEditor] Could not render PDF to image via PDF.js:", err);
    return null;
  }
};

/**
 * Fetch image bytes from URL or Base64 data URL
 */
export const fetchImageBytes = async (url) => {
  if (!url) return null;
  try {
    let targetUrl = url;
    if (
      !targetUrl.startsWith("http://") &&
      !targetUrl.startsWith("https://") &&
      !targetUrl.startsWith("data:") &&
      !targetUrl.startsWith("blob:")
    ) {
      const signed = await getLetterheadSignedUrl(targetUrl);
      if (signed) targetUrl = signed;
    }

    // Convert any image (SVG, WebP, JPEG, PNG, CORS image) to a standard PNG via canvas
    if (typeof document !== "undefined") {
      try {
        const pngDataUrl = await new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            try {
              const canvas = document.createElement("canvas");
              canvas.width = img.naturalWidth || img.width || 200;
              canvas.height = img.naturalHeight || img.height || 80;
              const ctx = canvas.getContext("2d");
              ctx.drawImage(img, 0, 0);
              resolve(canvas.toDataURL("image/png"));
            } catch (err) {
              reject(err);
            }
          };
          img.onerror = (e) => reject(e);
          img.src = targetUrl;
        });

        if (pngDataUrl && pngDataUrl.startsWith("data:image/png")) {
          const parts = pngDataUrl.split(",");
          const binary = atob(parts[1]);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
          }
          return { bytes, ext: "png" };
        }
      } catch (canvasErr) {
        console.warn("[fetchImageBytes] Canvas PNG conversion fallback to direct fetch:", canvasErr);
      }
    }

    if (targetUrl.startsWith("data:image/")) {
      const parts = targetUrl.split(",");
      const mimeMatch = targetUrl.match(/data:image\/([a-zA-Z0-9+]+);/);
      const ext = mimeMatch ? (mimeMatch[1].includes("jpeg") || mimeMatch[1].includes("jpg") ? "jpeg" : "png") : "png";
      const binary = atob(parts[1]);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      return { bytes, ext };
    }

    const res = await fetch(targetUrl, { mode: "cors" });
    if (!res.ok) return null;
    const blob = await res.blob();
    const buf = await blob.arrayBuffer();
    let ext = "png";
    if (blob.type.includes("jpeg") || blob.type.includes("jpg")) ext = "jpeg";
    else if (blob.type.includes("png")) ext = "png";
    return { bytes: new Uint8Array(buf), ext };
  } catch (e) {
    console.warn("Could not fetch logo image directly:", e);
    return null;
  }
};

/**
 * Trigger native browser Print-to-PDF flow for exact 1:1 editor fidelity via isolated iframe
 */
export const printOfferLetterDocument = () => {
  if (typeof document === "undefined" || typeof window === "undefined") return;

  const pageElements = document.querySelectorAll(
    ".offer-letter-document-editor-container .docx-page, " +
    ".offer-letter-document-editor-container .docx-editor-page, " +
    ".offer-letter-document-editor-container [data-docx-page], " +
    ".offer-letter-document-editor-container [data-testid='docx-page']"
  );

  if (!pageElements || pageElements.length === 0) {
    window.print();
    return;
  }

  // Create isolated iframe for clean multi-page printing without app shell
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.id = "offer-letter-print-frame";

  document.body.appendChild(iframe);

  const doc = iframe.contentWindow.document;
  doc.open();

  // Collect all styles from the parent document
  let stylesHtml = "";
  document.querySelectorAll("link[rel='stylesheet'], style").forEach((node) => {
    stylesHtml += node.outerHTML;
  });

  // Extract each page's inner HTML and inline background
  let pagesHtml = "";
  pageElements.forEach((el) => {
    const bgImage = el.style.backgroundImage || getComputedStyle(el).backgroundImage || "";
    const bgSize = el.style.backgroundSize || "100% 100%";
    const bgRepeat = el.style.backgroundRepeat || "no-repeat";
    const bgPosition = el.style.backgroundPosition || "center top";

    pagesHtml += `
      <div class="docx-print-sheet" style="
        position: relative;
        width: 210mm;
        height: 297mm;
        box-sizing: border-box;
        overflow: hidden;
        page-break-after: always;
        break-after: page;
        margin: 0 auto;
        padding: 0;
        background-color: #ffffff;
        ${bgImage && bgImage !== "none" ? `background-image: ${bgImage} !important; background-size: ${bgSize} !important; background-repeat: ${bgRepeat} !important; background-position: ${bgPosition} !important;` : ""}
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      ">
        ${el.innerHTML}
      </div>
    `;
  });

  doc.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Offer Letter</title>
        ${stylesHtml}
        <style>
          @page {
            size: A4 portrait;
            margin: 0;
          }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: #ffffff !important;
            width: 210mm;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .docx-print-sheet {
            width: 210mm !important;
            height: 297mm !important;
            page-break-after: always !important;
            break-after: page !important;
            box-sizing: border-box !important;
            overflow: hidden !important;
          }
          .docx-table-furniture,
          .docx-cursor,
          .docx-selection,
          .layout-table-row-resize-handle,
          .docx-table-edge-handle-right,
          .docx-table-divider-handle,
          .offer-letter-variable-top-inline {
            display: none !important;
          }
        </style>
      </head>
      <body>
        ${pagesHtml}
      </body>
    </html>
  `);
  doc.close();

  setTimeout(() => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => {
      if (iframe.parentNode) {
        document.body.removeChild(iframe);
      }
    }, 2500);
  }, 300);
};

/**
 * Generate high-quality A4 PDF supporting both Letterhead mode (PDF background) and Normal mode (Logo + Header + Footer)
 */
export const generateOfferLetterPdf = async ({
  content = "",
  docBytes = null,
  isLetterhead = false,
  header_footer_mode = "OUR_HEADER_FOOTER",
  letterHeadData = null,
  companylogo = null,
  headerTitle = "",
  footerText = "",
  payConfigContent = "",
  isCtcZero = false,
  fieldValues = null,
  variables = null,
  watermark = null,
  selectedKeys = null,
}) => {
  // Instantly mount loading overlay with 0ms delay as soon as clicked
  let freezeOverlay = null;
  let editorContainer = null;
  if (typeof document !== "undefined") {
    editorContainer =
      document.querySelector(".offer-letter-document-editor-container") ||
      document.querySelector(".offer-letter-document-editor");
    if (editorContainer) {
      freezeOverlay = document.createElement("div");
      freezeOverlay.id = "docx-export-freeze-overlay";
      freezeOverlay.style.position = "absolute";
      freezeOverlay.style.top = "0";
      freezeOverlay.style.left = "0";
      freezeOverlay.style.width = "100%";
      freezeOverlay.style.height = "100%";
      freezeOverlay.style.backgroundColor = "rgba(255, 255, 255, 0.88)";
      freezeOverlay.style.backdropFilter = "blur(3px)";
      freezeOverlay.style.zIndex = "999999";
      freezeOverlay.style.display = "flex";
      freezeOverlay.style.flexDirection = "column";
      freezeOverlay.style.alignItems = "center";
      freezeOverlay.style.justifyContent = "center";
      freezeOverlay.style.gap = "12px";
      freezeOverlay.style.pointerEvents = "all";
      freezeOverlay.innerHTML = `
        <div style="width: 34px; height: 34px; border: 3px solid #e2e8f0; border-top-color: #8b44ff; border-radius: 50%; animation: docx-spin 0.7s linear infinite;"></div>
        <div style="font-family: inherit; font-size: 13px; font-weight: 600; color: #475569;">Generating PDF...</div>
        <style>@keyframes docx-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }</style>
      `;
      editorContainer.appendChild(freezeOverlay);
    }
  }

  const applyWatermarkToPdf = (targetPdf) => {
    if (!watermark || !targetPdf) return targetPdf;
    try {
      const totalPages = targetPdf.internal.getNumberOfPages();
      for (let p = 1; p <= totalPages; p++) {
        targetPdf.setPage(p);
        if (targetPdf.saveGraphicsState) targetPdf.saveGraphicsState();
        targetPdf.setFont("helvetica", "bold");
        targetPdf.setFontSize(55);
        targetPdf.setTextColor(210, 215, 225);
        targetPdf.text(String(watermark), 105, 148, {
          align: "center",
          angle: 45,
        });
        if (targetPdf.restoreGraphicsState) targetPdf.restoreGraphicsState();
      }
    } catch (e) {
      console.warn("[generateOfferLetterPdf] Watermark render failed:", e);
    }
    return targetPdf;
  };

  const effectiveFieldValues = fieldValues ?? null;
  const JSZip = (await import("jszip")).default;
  const {
    parseOfferLetterContent,
    extractDocumentStructureFromDocxXml,
    base64ToUint8Array,
    uint8ArrayToBase64,
    substituteVariablesInDocxXml,
    htmlToDocxXml,
    syncHeaderAndFooterInDocxZip,
    stripAnnexureFromDocXml,
    stripAnnexureFromHtml,
    syncAnnexureTableInDocXml,
  } = await import("./editorDocxUtils");

  const parsed = parseOfferLetterContent(content);
  const isLetterheadMode = Boolean(
    isLetterhead ||
    header_footer_mode === "LETTERHEAD" ||
    parsed?.header_footer_mode === "LETTERHEAD"
  );

  let letterheadDataUrl = null;
  const effectiveLetterHead = Array.isArray(letterHeadData) ? letterHeadData[0] : letterHeadData;
  const lPath = effectiveLetterHead?.letterhead_file_path || effectiveLetterHead?.letterhead_path;

  if (isLetterheadMode && lPath) {
    try {
      const signedUrl = await getLetterheadSignedUrl(lPath);
      if (signedUrl) {
        letterheadDataUrl = await loadPdfFirstPageAsDataUrl(signedUrl);
      }
    } catch (e) {
      console.warn("[generateOfferLetterPdf] Could not load letterhead for PDF:", e);
    }
  }

  const jsPDF = (await import("jspdf")).jsPDF || (await import("jspdf")).default;

  // 1. Direct DOM Page Snapshot Engine (100% pixel-perfect match to Editor & Preview across all pages)
  if (editorContainer) {
    const allDescendants = Array.from(editorContainer.querySelectorAll("*"));
    const scrollContainers = allDescendants.filter((el) => {
      const style = window.getComputedStyle(el);
      const isScroll =
        style.overflowY === "auto" ||
        style.overflowY === "scroll" ||
        style.overflow === "auto" ||
        style.overflow === "scroll" ||
        el.scrollHeight > el.clientHeight + 20;
      return (
        isScroll ||
        el.classList?.contains("docx-editor__scroll-container") ||
        el.classList?.contains("docx-workspace") ||
        el.classList?.contains("docx-editor") ||
        el.classList?.contains("docx-pages")
      );
    });

    const originalScrollTop = editorContainer.scrollTop;
    const originalStyles = scrollContainers.map((el) => ({
      el,
      height: el.style.height,
      maxHeight: el.style.maxHeight,
      overflow: el.style.overflow,
      overflowY: el.style.overflowY,
      scrollTop: el.scrollTop,
    }));

    try {
      // Find main scroller to simulate scroll warmup across virtualized pages
      const mainScroller = scrollContainers[0] || editorContainer;
      const targetMaxScroll = mainScroller.scrollHeight || 6000;

      // Temporarily expand scroll containers in background so all virtualized pages mount in DOM
      scrollContainers.forEach((el) => {
        el.style.setProperty("height", "auto", "important");
        el.style.setProperty("max-height", "none", "important");
        el.style.setProperty("overflow", "visible", "important");
        el.style.setProperty("overflow-y", "visible", "important");
      });

      // Rapidly step through scroll checkpoints so virtual DOM instantiates all document pages behind overlay
      for (let sY = 0; sY <= targetMaxScroll; sY += 600) {
        if (mainScroller.scrollTo) {
          mainScroller.scrollTo(0, sY);
        }
      }
      if (mainScroller.scrollTo) {
        mainScroller.scrollTo(0, 0);
      }

      // Wait for reflow and full page mount behind overlay
      await new Promise((resolve) => setTimeout(resolve, 250));

      let pageElements = Array.from(
        editorContainer.querySelectorAll(
          ".docx-editor-page, .docx-page, [data-docx-page], [data-testid='docx-page'], .docx-editor__page, .docx-sheet, [data-page-index], [data-page], .docx-pages > div"
        )
      ).filter(
        (el) =>
          el !== freezeOverlay &&
          !freezeOverlay.contains(el) &&
          el.offsetWidth > 100 &&
          el.offsetHeight > 100
      );

      // Deduplicate nested page containers
      const uniquePages = pageElements.filter((el, idx, arr) => {
        return !arr.some((other, otherIdx) => otherIdx !== idx && other.contains(el));
      });

      if (uniquePages.length > 0) {
        const html2canvas = (await import("html2canvas")).default || (await import("html2canvas"));
        const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
        const pageWidth = 210;
        const pageHeight = 297;

        const originalGetComputedStyle = window.getComputedStyle;
        window.getComputedStyle = function (el, pseudo) {
          const style = originalGetComputedStyle.call(window, el, pseudo);
          if (!style) return style;
          return new Proxy(style, {
            get(target, prop) {
              const val = target[prop];
              if (typeof val === "string" && (val.includes("color(") || val.includes("oklch") || val.includes("lab(") || val.includes("lch("))) {
                return typeof prop === "string" && prop.toLowerCase().includes("background")
                  ? "rgb(255, 255, 255)"
                  : "rgb(30, 41, 59)";
              }
              if (typeof val === "function") {
                return function (...args) {
                  const res = val.apply(target, args);
                  if (typeof res === "string" && (res.includes("color(") || res.includes("oklch") || res.includes("lab(") || res.includes("lch("))) {
                    return args[0] && typeof args[0] === "string" && args[0].toLowerCase().includes("background")
                      ? "rgb(255, 255, 255)"
                      : "rgb(30, 41, 59)";
                  }
                  return res;
                };
              }
              return val;
            },
          });
        };

        try {
          for (let i = 0; i < uniquePages.length; i++) {
            const pageEl = uniquePages[i];

            if (i > 0) {
              pdf.addPage();
            }

            const canvas = await html2canvas(pageEl, {
              scale: 2,
              useCORS: true,
              logging: false,
              allowTaint: true,
              backgroundColor: "#ffffff",
              onclone: (clonedDoc) => {
                // Sanitize all style tags inside cloned doc to remove unsupported color() functions
                const styleTags = clonedDoc.querySelectorAll("style");
                styleTags.forEach((s) => {
                  if (s.innerHTML && (s.innerHTML.includes("color(") || s.innerHTML.includes("oklch") || s.innerHTML.includes("lab("))) {
                    s.innerHTML = s.innerHTML
                      .replace(/color\([^\)]+\)/g, "#1e293b")
                      .replace(/oklch\([^\)]+\)/g, "#1e293b")
                      .replace(/lab\([^\)]+\)/g, "#1e293b");
                  }
                });

                if (fieldValues && typeof fieldValues === "object") {
                  const replacements = [];
                  for (const [k, v] of Object.entries(fieldValues)) {
                    if (v === undefined || v === null) continue;
                    const cleanKey = k.replace(/^@\{?/, "").replace(/\}?$/, "").trim();
                    replacements.push([`@{${cleanKey}}`, String(v)]);
                  }
                  if (replacements.length > 0) {
                    const walker = clonedDoc.createTreeWalker(
                      clonedDoc.body || clonedDoc,
                      NodeFilter.SHOW_TEXT,
                      null,
                      false
                    );
                    let textNode;
                    while ((textNode = walker.nextNode())) {
                      for (const [placeholder, val] of replacements) {
                        if (textNode.nodeValue && textNode.nodeValue.includes(placeholder)) {
                          textNode.nodeValue = textNode.nodeValue.split(placeholder).join(val);
                          if (textNode.parentElement && textNode.parentElement.style) {
                            if (textNode.parentElement.style.backgroundColor === "yellow" || textNode.parentElement.style.backgroundColor === "rgb(255, 255, 0)") {
                              textNode.parentElement.style.backgroundColor = "transparent";
                            }
                          }
                        }
                      }
                    }
                  }
                }

                // Sanitize all elements inline styles
                const allNodes = clonedDoc.querySelectorAll("*");
                for (let n = 0; n < allNodes.length; n++) {
                  const node = allNodes[n];
                  if (node.style && node.style.cssText) {
                    if (node.style.cssText.includes("color(") || node.style.cssText.includes("oklch") || node.style.cssText.includes("lab(")) {
                      node.style.cssText = node.style.cssText
                        .replace(/color\([^\)]+\)/g, "#1e293b")
                        .replace(/oklch\([^\)]+\)/g, "#1e293b")
                        .replace(/lab\([^\)]+\)/g, "#1e293b");
                    }
                  }
                }
              },
              ignoreElements: (element) => {
                return (
                  element === freezeOverlay ||
                  element.id === "docx-export-freeze-overlay" ||
                  element.classList?.contains("docx-table-furniture") ||
                  element.classList?.contains("docx-cursor") ||
                  element.classList?.contains("docx-selection") ||
                  element.classList?.contains("layout-table-row-resize-handle") ||
                  element.classList?.contains("docx-table-edge-handle-right") ||
                  element.classList?.contains("docx-table-divider-handle") ||
                  element.classList?.contains("offer-letter-variable-top-inline") ||
                  element.classList?.contains("meepl-docx-page-boundary-guide")
                );
              },
            });

            const imgData = canvas.toDataURL("image/jpeg", 0.98);
            pdf.addImage(imgData, "JPEG", 0, 0, pageWidth, pageHeight);
          }

          return pdf;
        } finally {
          window.getComputedStyle = originalGetComputedStyle;
        }
      }
    } catch (domErr) {
      console.warn("[generateOfferLetterPdf] DOM snapshot failed, falling back to AST parser:", domErr);
    } finally {
      if (freezeOverlay.parentNode) {
        freezeOverlay.parentNode.removeChild(freezeOverlay);
      }
      originalStyles.forEach(({ el, height, maxHeight, overflow, overflowY, scrollTop }) => {
        el.style.height = height;
        el.style.maxHeight = maxHeight;
        el.style.overflow = overflow;
        el.style.overflowY = overflowY;
        el.scrollTop = scrollTop;
      });
      editorContainer.scrollTop = originalScrollTop;
    }
  }

  let items = [];

  const injectPayConfigToDocXml = (docXml) => {
    if (isCtcZero) {
      return stripAnnexureFromDocXml(docXml || "");
    }
    return docXml || "";
  };

  let embeddedLogoDataUrl = null;
  let embeddedHeaderTitle = null;
  let embeddedFooterText = null;

  const extractNormalHeaderFooterFromZip = async (zip) => {
    if (isLetterheadMode || !zip) return;
    try {
      if (!companylogo && !embeddedLogoDataUrl) {
        const imgFile = zip.file("word/media/image1.png") || zip.file("word/media/image1.jpeg") || zip.file("word/media/image1.jpg");
        if (imgFile) {
          const imgBytes = await imgFile.async("uint8array");
          if (imgBytes && imgBytes.length > 0) {
            const ext = imgFile.name.endsWith(".jpeg") || imgFile.name.endsWith(".jpg") ? "jpeg" : "png";
            embeddedLogoDataUrl = `data:image/${ext};base64,${uint8ArrayToBase64(imgBytes)}`;
          }
        }
      }
      if (!headerTitle && !embeddedHeaderTitle) {
        const hFile = zip.file("word/header1.xml");
        if (hFile) {
          const hXml = await hFile.async("text");
          const match = hXml.match(/<w:sdtContent>[\s\S]*?<w:t[^>]*>([\s\S]*?)<\/w:t>/i);
          if (match && match[1]) {
            embeddedHeaderTitle = match[1].trim();
          }
        }
      }
      if (!footerText && !embeddedFooterText) {
        const fFile = zip.file("word/footer1.xml");
        if (fFile) {
          const fXml = await fFile.async("text");
          const tMatches = fXml.match(/<w:t[^>]*>([\s\S]*?)<\/w:t>/gi);
          if (tMatches && tMatches.length > 0) {
            const lines = tMatches.map((m) => m.replace(/<\/?w:t[^>]*>/gi, "").trim()).filter(Boolean);
            if (lines.length > 0) {
              embeddedFooterText = lines.join("\n");
            }
          }
        }
      }
    } catch (e) {
      console.warn("[generateOfferLetterPdf] Error extracting embedded header/footer from zip:", e);
    }
  };

  // Helper to extract media images from docx zip
  const extractMediaMapFromZip = async (zip) => {
    const mediaMap = new Map();
    try {
      const relsFile = zip.file("word/_rels/document.xml.rels");
      if (relsFile) {
        const relsXml = await relsFile.async("text");
        const relMatches = relsXml.matchAll(/<Relationship[^>]*Id="([^"]+)"[^>]*Target="([^"]+)"/gi);
        for (const m of relMatches) {
          const rId = m[1];
          let target = m[2];
          if (target.startsWith("media/")) target = `word/${target}`;
          else if (!target.startsWith("word/")) target = `word/${target}`;
          const mFile = zip.file(target);
          if (mFile) {
            const mBytes = await mFile.async("uint8array");
            const ext = target.endsWith(".png") ? "png" : "jpeg";
            mediaMap.set(rId, `data:image/${ext};base64,${uint8ArrayToBase64(mBytes)}`);
          }
        }
      }
    } catch (e) {
      console.warn("[generateOfferLetterPdf] Error building mediaMap:", e);
    }
    return mediaMap;
  };

  // 1. Extract AST from docBytes
  if (docBytes) {
    try {
      const zip = await JSZip.loadAsync(docBytes);
      await extractNormalHeaderFooterFromZip(zip);
      const mediaMap = await extractMediaMapFromZip(zip);
      let docXml = await zip.file("word/document.xml")?.async("text");
      let numberingXml = (await zip.file("word/numbering.xml")?.async("text")) || "";
      if (docXml) {
        if (effectiveFieldValues && Object.keys(effectiveFieldValues).length > 0) {
          const activeKeys = selectedKeys || parsed?.selectedKeys || null;
          docXml = syncAnnexureTableInDocXml(docXml, effectiveFieldValues, null, null, activeKeys);
          docXml = substituteVariablesInDocxXml(docXml, effectiveFieldValues);
        }
        docXml = injectPayConfigToDocXml(docXml);
        items = extractDocumentStructureFromDocxXml(docXml, numberingXml, mediaMap);
      }
    } catch (e) {
      console.warn("[generateOfferLetterPdf] Error extracting from docBytes:", e);
    }
  }

  // 2. Extract AST from parsed docxBase64
  if (items.length === 0 && parsed?.docxBase64) {
    try {
      const bytes = base64ToUint8Array(parsed.docxBase64);
      const zip = await JSZip.loadAsync(bytes);
      await extractNormalHeaderFooterFromZip(zip);
      const mediaMap = await extractMediaMapFromZip(zip);
      let docXml = await zip.file("word/document.xml")?.async("text");
      let numberingXml = (await zip.file("word/numbering.xml")?.async("text")) || "";
      if (docXml) {
        if (effectiveFieldValues && Object.keys(effectiveFieldValues).length > 0) {
          const activeKeys = selectedKeys || parsed?.selectedKeys || null;
          docXml = syncAnnexureTableInDocXml(docXml, effectiveFieldValues, null, null, activeKeys);
          docXml = substituteVariablesInDocxXml(docXml, effectiveFieldValues);
        }
        docXml = injectPayConfigToDocXml(docXml);
        items = extractDocumentStructureFromDocxXml(docXml, numberingXml, mediaMap);
      }
    } catch (e) {
      console.warn("[generateOfferLetterPdf] Error extracting from docxBase64:", e);
    }
  }

  // 3. Fallback to HTML parsing
  if (items.length === 0) {
    let offerHtml = (parsed?.html || (typeof content === "string" ? content : "") || "");
    if (effectiveFieldValues && Object.keys(effectiveFieldValues).length > 0) {
      for (const [k, v] of Object.entries(effectiveFieldValues)) {
        if (v === undefined || v === null) continue;
        const rawKey = k.replace(/^@\{?/, "").replace(/\}?$/, "").trim();
        offerHtml = offerHtml.split(`@{${rawKey}}`).join(String(v));
      }
    }
    let rawHtml = offerHtml;
    if (isCtcZero) {
      rawHtml = stripAnnexureFromHtml(rawHtml);
    }
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = rawHtml || "Offer Letter Document Content";

    const parseHtmlNodes = (container) => {
      for (let i = 0; i < container.childNodes.length; i++) {
        const node = container.childNodes[i];
        if (node.nodeType === 3) {
          const text = node.textContent?.trim();
          if (text) {
            items.push({
              type: "paragraph",
              align: "left",
              runs: [{ text, bold: false, fontSizePt: 10.5 }],
              text,
              spaceBefore: 0,
              spaceAfter: 1.8,
              lineSpacing: 1.15,
            });
          }
        } else if (node.nodeType === 1) {
          const tag = node.tagName.toLowerCase();
          if (tag === "hr") {
            const isPageBreak = node.classList?.contains("page-break") ||
              node.getAttribute("class")?.includes("page-break") ||
              node.style?.pageBreakAfter === "always" ||
              node.style?.pageBreakBefore === "always";
            if (isPageBreak) {
              items.push({ type: "pageBreak" });
            }
          } else if (tag === "table") {
            const rows = [];
            node.querySelectorAll("tr").forEach((tr) => {
              const cells = [];
              tr.querySelectorAll("td, th").forEach((td) => {
                const isHeader = td.tagName.toLowerCase() === "th" || Boolean(td.querySelector("b, strong"));
                cells.push({
                  text: td.textContent?.trim() || "",
                  fillColor: isHeader ? "#F1F5F9" : null,
                  paragraphs: [{
                    align: td.style.textAlign || "left",
                    runs: [{
                      text: td.textContent?.trim() || "",
                      bold: isHeader,
                      fontSizePt: 9.5,
                    }],
                  }],
                });
              });
              if (cells.length > 0) rows.push({ cells });
            });
            if (rows.length > 0) items.push({ type: "table", rows });
          } else if (tag === "p" || tag === "h1" || tag === "h2" || tag === "h3" || tag === "h4") {
            const text = node.textContent?.trim();
            if (text) {
              const isCtcTitle = text.toLowerCase().includes("annexure") || text.toLowerCase().includes("ctc details");
              const isHeading = tag.startsWith("h") || isCtcTitle;
              const fontSizePt = isCtcTitle ? 14 : tag === "h1" ? 16 : tag === "h2" ? 14 : tag === "h3" ? 12 : 10.5;
              items.push({
                type: "paragraph",
                pageBreakBefore: isCtcTitle,
                align: isCtcTitle ? "center" : (node.style.textAlign || "left"),
                runs: [{ text, bold: Boolean(node.querySelector("b, strong") || isHeading), fontSizePt }],
                text,
                spaceBefore: isHeading ? 3.0 : 0,
                spaceAfter: isHeading ? 2.5 : 1.8,
                lineSpacing: 1.15,
              });
            }
          } else if (tag === "div" || tag === "section" || tag === "article") {
            parseHtmlNodes(node);
          } else {
            const text = node.textContent?.trim();
            if (text) {
              items.push({
                type: "paragraph",
                align: "left",
                runs: [{ text, bold: false, fontSizePt: 10.5 }],
                text,
                spaceBefore: 0,
                spaceAfter: 1.8,
                lineSpacing: 1.15,
              });
            }
          }
        }
      }
    };

    parseHtmlNodes(tempDiv);
  }

  if (items.length === 0) {
    items.push({
      type: "paragraph",
      align: "left",
      runs: [{ text: "Offer Letter Document Content", bold: false, fontSizePt: 10.5 }],
      text: "Offer Letter Document Content",
      spaceBefore: 0,
      spaceAfter: 1.8,
      lineSpacing: 1.15,
    });
  }

  const pdf = new jsPDF({
    unit: "mm",
    format: "a4",
    orientation: "portrait",
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  const effectiveFooter = isLetterheadMode ? "" : (footerText || embeddedFooterText || letterHeadData?.letterhead_footer || letterHeadData?.[0]?.letterhead_footer || "");
  const effectiveHeader = isLetterheadMode ? "" : (headerTitle || embeddedHeaderTitle || letterHeadData?.letterhead_header || letterHeadData?.[0]?.letterhead_header || "");

  let logoDataUrl = "";
  if (companylogo && !isLetterheadMode) {
    try {
      const imgInfo = await fetchImageBytes(companylogo);
      if (imgInfo?.bytes) {
        logoDataUrl = `data:image/${imgInfo.ext};base64,${uint8ArrayToBase64(imgInfo.bytes)}`;
      } else {
        logoDataUrl = companylogo;
      }
    } catch (e) {
      logoDataUrl = companylogo;
    }
  }
  if (!logoDataUrl && embeddedLogoDataUrl && !isLetterheadMode) {
    logoDataUrl = embeddedLogoDataUrl;
  }
  const hasHeader = !isLetterheadMode && Boolean(effectiveHeader || logoDataUrl || companylogo);

  const pageSetup = items.pageSetup || {};
  const isNarrow = parsed?.margin_preset === "narrow" || parsed?.left_margin === 12.7 || pageSetup.leftMarginMm === 12.7;
  const defaultSide = isNarrow ? 12.7 : 20;
  const defaultTop = isNarrow ? 12.7 : 20;
  const defaultBottom = isNarrow ? 12.7 : 20;

  const topMargin = parsed?.top_margin
    ? Math.max(parsed.top_margin, 15)
    : (pageSetup.topMarginMm
    ? Math.max(pageSetup.topMarginMm, 15)
    : (isLetterheadMode ? 45 : (hasHeader ? 32 : defaultTop)));
  const bottomMargin = parsed?.bottom_margin
    ? Math.max(parsed.bottom_margin, 15)
    : (pageSetup.bottomMarginMm
    ? Math.max(pageSetup.bottomMarginMm, 15)
    : (isLetterheadMode ? 25 : (effectiveFooter ? 30 : defaultBottom)));
  const leftMargin = parsed?.left_margin || pageSetup.leftMarginMm || defaultSide;
  const rightMargin = parsed?.right_margin || pageSetup.rightMarginMm || defaultSide;
  const contentWidth = pageWidth - leftMargin - rightMargin;
  const maxY = pageHeight - bottomMargin;

  const hexToRgb = (hex) => {
    if (!hex) return null;
    const clean = hex.replace("#", "").trim();
    if (clean.length === 3) {
      return [
        parseInt(clean[0] + clean[0], 16),
        parseInt(clean[1] + clean[1], 16),
        parseInt(clean[2] + clean[2], 16),
      ];
    }
    if (clean.length === 6) {
      return [
        parseInt(clean.substring(0, 2), 16),
        parseInt(clean.substring(2, 4), 16),
        parseInt(clean.substring(4, 6), 16),
      ];
    }
    return null;
  };

  const drawPageDecorations = (pageNum) => {
    pdf.setPage(pageNum);

    // 1. Draw Letterhead Background (base layer beneath text)
    if (isLetterheadMode && letterheadDataUrl) {
      try {
        pdf.addImage(letterheadDataUrl, "PNG", 0, 0, pageWidth, pageHeight);
      } catch (e) {
        console.warn("Could not draw letterhead in PDF:", e);
      }
    }

    // 2. Normal Header & Logo
    if (hasHeader) {
      if (logoDataUrl) {
        try {
          const format = logoDataUrl.includes("png") ? "PNG" : "JPEG";
          pdf.addImage(logoDataUrl, format, leftMargin, 8, 26, 13);
        } catch (e) {
          console.warn("Could not draw logo in PDF:", e);
        }
      }

      if (effectiveHeader) {
        pdf.setFontSize(13);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(30, 41, 59);
        const headerLines = pdf.splitTextToSize(effectiveHeader, pageWidth - 70);
        pdf.text(headerLines, pageWidth / 2, 16, { align: "center" });
      }

      pdf.setDrawColor(203, 213, 225);
      pdf.setLineWidth(0.3);
      pdf.line(leftMargin, 25, pageWidth - rightMargin, 25);
    }

    // 3. Normal Footer
    if (effectiveFooter && !isLetterheadMode) {
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(100, 116, 139);
      const footerLines = pdf.splitTextToSize(
        effectiveFooter.replace(/<br\s*[\/]?>/gi, "\n").replace(/\\n/g, "\n"),
        pageWidth - 30
      );
      const fHeight = (Array.isArray(footerLines) ? footerLines.length : 1) * 4;
      const footerY = pageHeight - fHeight - 6;

      pdf.setDrawColor(203, 213, 225);
      pdf.setLineWidth(0.3);
      pdf.line(leftMargin, footerY - 4, pageWidth - rightMargin, footerY - 4);

      pdf.text(footerLines, pageWidth / 2, footerY, { align: "center" });
    }
  };

  // Draw background on Page 1
  drawPageDecorations(1);

  const addNewPage = () => {
    pdf.addPage();
    const newPageNum = pdf.internal.getNumberOfPages();
    drawPageDecorations(newPageNum);
    return topMargin + 2;
  };

  let currentY = topMargin + 2;

  for (const item of items) {
    if (item.type === "pageBreak") {
      currentY = addNewPage();
      continue;
    }

    if (item.type === "paragraph") {
      const primaryRun = item.runs?.[0] || {};
      const maxFontSizePt = Math.max(...(item.runs?.map((r) => r.fontSizePt || 10.5) || [10.5]));
      const isBold = item.runs?.some((r) => r.bold);
      const isItalic = item.runs?.some((r) => r.italic);
      const fontColor = primaryRun.fontColor || "#0f172a";

      pdf.setFont("helvetica", isBold && isItalic ? "bolditalic" : isBold ? "bold" : isItalic ? "italic" : "normal");
      pdf.setFontSize(maxFontSizePt);

      const rgb = hexToRgb(fontColor) || [15, 23, 42];
      pdf.setTextColor(rgb[0], rgb[1], rgb[2]);

      const indentLeft = item.indentLeft || 0;
      const indentRight = item.indentRight || 0;
      const hangingIndent = item.hangingIndent || 0;
      const firstLineIndent = item.firstLineIndent || 0;

      const pEffectiveWidth = Math.max(20, contentWidth - indentLeft - indentRight);
      const lines = pdf.splitTextToSize(item.text || "", pEffectiveWidth);

      const linePitchRatio = item.lineSpacing != null ? item.lineSpacing : 1.12;
      const computedLineHeight = item.lineSpacingExactPt != null
        ? item.lineSpacingExactPt * 0.352778
        : (maxFontSizePt * 0.352778) * linePitchRatio;

      const pSpaceAfter = item.spaceAfter != null ? Math.min(item.spaceAfter, 1.4) : 1.2;
      const pSpaceBefore = item.spaceBefore != null ? item.spaceBefore : 0;
      const pBlockHeight = (lines.length > 0 && item.text?.trim())
        ? (lines.length * computedLineHeight + pSpaceAfter + pSpaceBefore)
        : (computedLineHeight + pSpaceAfter);

      if (item.hasPageBreak || item.pageBreakBefore || (currentY + pBlockHeight > maxY && currentY > topMargin + 4)) {
        currentY = addNewPage();
      }

      currentY += pSpaceBefore;

      if (lines.length > 0 && item.text?.trim()) {
        const align = item.align || "left";

        lines.forEach((line, lineIdx) => {
          if (currentY + computedLineHeight > maxY) {
            currentY = addNewPage();
          }

          let textX = leftMargin + indentLeft;

          if (lineIdx === 0) {
            if (firstLineIndent) {
              textX += firstLineIndent;
            } else if (hangingIndent) {
              textX -= hangingIndent;
            }
          }

          if (align === "center") {
            textX = leftMargin + (contentWidth / 2);
          } else if (align === "right") {
            textX = leftMargin + contentWidth - indentRight;
          }

          pdf.text(line, textX, currentY, { align: align === "justify" ? "left" : align });
          currentY += computedLineHeight;
        });
        currentY += pSpaceAfter;
      } else {
        currentY += computedLineHeight * 0.8;
      }
    } else if (item.type === "image" && item.src) {
      const imgWidth = item.widthMm || 45;
      const imgHeight = item.heightMm || 22;
      const wrapMode = item.wrapMode || "inline";

      if (wrapMode === "behind" || wrapMode === "inFront") {
        const imgX = item.align === "right" ? (leftMargin + contentWidth - imgWidth) : item.align === "center" ? (leftMargin + (contentWidth - imgWidth) / 2) : leftMargin;
        pdf.addImage(item.src, "PNG", imgX, currentY, imgWidth, imgHeight);
      } else if (wrapMode === "square" || wrapMode === "tight") {
        const imgX = wrapMode === "tight" ? (leftMargin + contentWidth - imgWidth) : leftMargin;
        pdf.addImage(item.src, "PNG", imgX, currentY, imgWidth, imgHeight);
        currentY += imgHeight + 2;
      } else {
        if (currentY + imgHeight > maxY && currentY > topMargin + 4) {
          currentY = addNewPage();
        }
        const imgX = item.align === "center" ? (leftMargin + (contentWidth - imgWidth) / 2) : item.align === "right" ? (leftMargin + contentWidth - imgWidth) : leftMargin;
        pdf.addImage(item.src, "PNG", imgX, currentY, imgWidth, imgHeight);
        currentY += imgHeight + 2;
      }
    } else if (item.type === "table" && item.rows?.length > 0) {
      const numCols = Math.max(...item.rows.map((r) => Array.isArray(r) ? r.length : (r.cells?.length || 0)));
      if (numCols === 0) continue;

      let colWidths = [];
      if (item.gridCols && item.gridCols.length === numCols) {
        const totalGrid = item.gridCols.reduce((a, b) => a + b, 0);
        if (totalGrid > 0) {
          colWidths = item.gridCols.map((w) => (w / totalGrid) * contentWidth);
        }
      }
      if (colWidths.length === 0) {
        if (numCols === 4) {
          colWidths = [contentWidth * 0.07, contentWidth * 0.53, contentWidth * 0.20, contentWidth * 0.20];
        } else if (numCols === 3) {
          colWidths = [contentWidth * 0.50, contentWidth * 0.25, contentWidth * 0.25];
        } else if (numCols === 2) {
          colWidths = [contentWidth * 0.50, contentWidth * 0.50];
        } else {
          const w = contentWidth / numCols;
          colWidths = Array(numCols).fill(w);
        }
      }

      const cellPad = 2.0;

      for (const row of item.rows) {
        const rawCells = Array.isArray(row) ? row : (row.cells || []);
        let maxCellLines = 1;
        let maxCellFontSizePt = 9.5;

        const cellData = rawCells.map((cell, cIdx) => {
          let text = "";
          let isBold = false;
          let cellFontSizePt = 9.5;
          let cellFontColor = "#1e293b";
          let cellFillColor = null;
          let pAlign = "left";

          if (typeof cell === "string") {
            text = cell.trim();
          } else if (cell) {
            text = cell.paragraphs?.map((p) => p.runs?.map((r) => r.text).join("") || "").join("\n") || cell.text || "";
            isBold = cell.paragraphs?.some((p) => p.runs?.some((r) => r.bold));
            const firstRun = cell.paragraphs?.[0]?.runs?.[0];
            if (firstRun?.fontSizePt) cellFontSizePt = firstRun.fontSizePt;
            if (firstRun?.fontColor) cellFontColor = firstRun.fontColor;
            if (cell.fillColor) cellFillColor = cell.fillColor;
            if (cell.paragraphs?.[0]?.align) pAlign = cell.paragraphs[0].align;
          }
          if (cellFontSizePt > maxCellFontSizePt) maxCellFontSizePt = cellFontSizePt;

          const cWidth = colWidths[cIdx] || colWidths[colWidths.length - 1];
          const lines = pdf.splitTextToSize(text, Math.max(10, cWidth - cellPad * 2));
          if (lines.length > maxCellLines) maxCellLines = lines.length;

          return { text, lines, isBold, cellFontSizePt, cellFontColor, cellFillColor, cWidth, pAlign };
        });

        const cellLineHeight = maxCellFontSizePt * 0.352778 * 1.15;
        const rowHeight = Math.max(5.8, maxCellLines * cellLineHeight + cellPad * 2);

        if (currentY + rowHeight > maxY) {
          currentY = addNewPage();
        }

        let curCellX = leftMargin;
        cellData.forEach((cell, cIdx) => {
          const cWidth = cell.cWidth;

          // Draw Cell Background Shading if present
          if (cell.cellFillColor) {
            const fillRgb = hexToRgb(cell.cellFillColor);
            if (fillRgb) {
              pdf.setFillColor(fillRgb[0], fillRgb[1], fillRgb[2]);
              pdf.rect(curCellX, currentY, cWidth, rowHeight, "F");
            }
          }

          // Draw Cell Border
          pdf.setDrawColor(203, 213, 225);
          pdf.setLineWidth(0.2);
          pdf.rect(curCellX, currentY, cWidth, rowHeight, "S");

          pdf.setFont("helvetica", cell.isBold ? "bold" : "normal");
          pdf.setFontSize(cell.cellFontSizePt || 9.5);

          const textRgb = hexToRgb(cell.cellFontColor) || [30, 41, 59];
          pdf.setTextColor(textRgb[0], textRgb[1], textRgb[2]);

          let cellTextY = currentY + cellPad + (cell.cellFontSizePt * 0.352778 * 0.85);
          const alignRight = cell.pAlign === "right" || (cIdx > 0 && numCols >= 2 && (/^[\d,\.\s]+$/.test(cell.text) || cell.text === "Monthly" || cell.text === "Annually"));

          cell.lines.forEach((line) => {
            if (alignRight) {
              pdf.text(line, curCellX + cWidth - cellPad, cellTextY, { align: "right" });
            } else if (cell.pAlign === "center") {
              pdf.text(line, curCellX + (cWidth / 2), cellTextY, { align: "center" });
            } else {
              pdf.text(line, curCellX + cellPad, cellTextY);
            }
            cellTextY += cellLineHeight;
          });

          curCellX += cWidth;
        });

        currentY += rowHeight;
      }
      currentY += 2.0;
    }
  }

  return applyWatermarkToPdf(pdf);
};

